import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Square, Headphones, ChevronUp, ChevronDown, X } from "lucide-react";

/**
 * ModuleAudio — sticky audio companion for curriculum modules.
 *
 * v1: uses browser-native Web Speech Synthesis API
 *     - Free, no API keys, offline-capable
 *     - Quality depends on the user's OS voices (Chrome on Win/Mac usually has solid German voices)
 *     - We strip markdown to give the synthesizer clean prose
 *
 * Upgrade path: a settings flag + premium voice prop can later route to
 * server-rendered ElevenLabs/OpenAI MP3 if licensed keys are provided.
 */

// ── Markdown → clean spoken text ────────────────────────────────────────
function markdownToSpeech(md) {
  if (!md) return "";
  return md
    // Remove front-matter (defensive — already stripped server-side)
    .replace(/^---[\s\S]*?---\s*/, "")
    // Code blocks → skip entirely
    .replace(/```[\s\S]*?```/g, " ")
    // Inline code → just the text
    .replace(/`([^`]+)`/g, "$1")
    // Images → alt text only
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    // Links → keep label
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // Headings → keep the text, drop the #
    .replace(/^#{1,6}\s+/gm, "")
    // Bold / italic / strikethrough markers
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    // Block quote markers
    .replace(/^>\s?/gm, "")
    // List bullets
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    // Horizontal rules
    .replace(/^[-*_]{3,}\s*$/gm, "")
    // Pipe tables → drop separator rows, keep cell text
    .replace(/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/gm, "")
    .replace(/\|/g, " — ")
    // Collapse whitespace
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+/g, " ")
    .trim();
}

// Split into chunks that respect sentence boundaries — many synth engines
// truncate or pause oddly past a few hundred characters. ~200-300 chars each.
function chunkText(text, target = 240) {
  if (!text) return [];
  const sentences = text.split(/(?<=[.!?…])\s+/);
  const chunks = [];
  let buf = "";
  for (const s of sentences) {
    if (!s) continue;
    if ((buf + " " + s).trim().length > target && buf) {
      chunks.push(buf.trim());
      buf = s;
    } else {
      buf = (buf + " " + s).trim();
    }
  }
  if (buf) chunks.push(buf.trim());
  return chunks;
}

export default function ModuleAudio({ body, title }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(null);
  const [rate, setRate] = useState(1.0);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [pos, setPos] = useState(0); // current chunk index
  const chunksRef = useRef([]);

  // Load voices (filter German first, fall back to all)
  useEffect(() => {
    if (!supported) return undefined;
    const refresh = () => {
      const all = window.speechSynthesis.getVoices();
      const de = all.filter((v) => /^de(-|_|$)/i.test(v.lang));
      const list = de.length ? de : all;
      setVoices(list);
      if (list.length && !voiceURI) {
        // Prefer a Microsoft / Google "Katja" / "Anna" / "Markus" if present
        const preferred =
          list.find((v) => /katja|anna|hedda|google deutsch/i.test(v.name)) ||
          list.find((v) => /^de-DE/i.test(v.lang)) ||
          list[0];
        setVoiceURI(preferred?.voiceURI || null);
      }
    };
    refresh();
    window.speechSynthesis.onvoiceschanged = refresh;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [supported, voiceURI]);

  // Prepare chunks
  const chunks = useMemo(() => chunkText(markdownToSpeech(body)), [body]);
  useEffect(() => { chunksRef.current = chunks; }, [chunks]);

  // Cancel on unmount or body change
  useEffect(() => {
    return () => {
      if (supported) {
        try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
      }
    };
  }, [supported, body]);

  const speakChunk = (i) => {
    if (!supported) return;
    const chunks = chunksRef.current;
    if (i >= chunks.length) {
      setPlaying(false);
      setPaused(false);
      setPos(0);
      return;
    }
    const u = new SpeechSynthesisUtterance(chunks[i]);
    const v = voices.find((vv) => vv.voiceURI === voiceURI);
    if (v) u.voice = v;
    u.lang = (v && v.lang) || "de-DE";
    u.rate = rate;
    u.pitch = 1.0;
    u.onend = () => speakChunk(i + 1);
    u.onerror = () => {
      // Skip past failed chunk
      speakChunk(i + 1);
    };
    setPos(i);
    try { window.speechSynthesis.speak(u); } catch (_) { /* noop */ }
  };

  const start = () => {
    if (!supported) return;
    try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    setPlaying(true);
    setPaused(false);
    // Small delay to ensure cancel() flushed in some engines
    setTimeout(() => speakChunk(pos || 0), 50);
  };

  const pause = () => {
    if (!supported) return;
    try { window.speechSynthesis.pause(); } catch (_) { /* noop */ }
    setPaused(true);
  };

  const resume = () => {
    if (!supported) return;
    try { window.speechSynthesis.resume(); } catch (_) { /* noop */ }
    setPaused(false);
  };

  const stop = () => {
    if (!supported) return;
    try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    setPlaying(false);
    setPaused(false);
    setPos(0);
  };

  // Hide entirely if browser doesn't support
  if (!supported || hidden) return null;

  const progressPct = chunks.length ? Math.round((pos / chunks.length) * 100) : 0;

  return (
    <div
      className="fixed bottom-6 right-6 z-[110] rounded-2xl shadow-2xl border backdrop-blur-md"
      style={{
        background: "rgba(14,26,48,0.92)",
        borderColor: "#D4AF3755",
        width: collapsed ? 64 : 340,
        maxWidth: "calc(100vw - 3rem)",
        transition: "width 220ms ease",
      }}
      data-testid="module-audio"
    >
      {/* Collapsed: just headphones bubble */}
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ color: "#D4AF37" }}
          data-testid="module-audio-expand"
          title="Audio-Player öffnen"
        >
          <Headphones size={22} />
        </button>
      ) : (
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <Headphones size={14} style={{ color: "#D4AF37" }} />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">Hör-Modus</div>
                <div className="serif text-sm text-white truncate" title={title}>{title || "Modul"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setCollapsed(true)}
                className="w-7 h-7 flex items-center justify-center text-white/50 hover:text-white"
                data-testid="module-audio-collapse"
                title="Einklappen"
              >
                <ChevronDown size={14} />
              </button>
              <button
                onClick={() => { stop(); setHidden(true); }}
                className="w-7 h-7 flex items-center justify-center text-white/50 hover:text-white"
                data-testid="module-audio-close"
                title="Schließen"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="h-1 rounded-full bg-white/10 overflow-hidden mb-3">
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{ width: `${progressPct}%`, background: "#D4AF37" }}
              data-testid="module-audio-progress"
            />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 mb-3">
            {!playing || (!paused && !window.speechSynthesis.speaking) ? (
              <button
                onClick={start}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#D4AF37] text-[#0E1A30] hover:opacity-90 transition font-medium text-sm"
                data-testid="module-audio-play"
              >
                <Play size={14} className="fill-current ml-0.5" /> Vorlesen
              </button>
            ) : paused ? (
              <button
                onClick={resume}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#D4AF37] text-[#0E1A30] hover:opacity-90 transition font-medium text-sm"
                data-testid="module-audio-resume"
              >
                <Play size={14} className="fill-current ml-0.5" /> Fortsetzen
              </button>
            ) : (
              <button
                onClick={pause}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white border border-white/15 hover:border-[#D4AF37] transition font-medium text-sm"
                data-testid="module-audio-pause"
              >
                <Pause size={14} /> Pause
              </button>
            )}
            <button
              onClick={stop}
              disabled={!playing}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/70 hover:text-white border border-white/10 disabled:opacity-40 transition"
              data-testid="module-audio-stop"
              title="Stop"
            >
              <Square size={12} className="fill-current" />
            </button>
          </div>

          {/* Settings: voice + speed */}
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/45 block mb-1">Stimme</span>
              <select
                value={voiceURI || ""}
                onChange={(e) => setVoiceURI(e.target.value)}
                className="w-full bg-white/[0.05] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#D4AF37] focus:outline-none"
                data-testid="module-audio-voice"
              >
                {voices.length === 0 && <option>Keine Stimme verfügbar</option>}
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>{v.name} {v.lang ? `(${v.lang})` : ""}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/45 block mb-1">Tempo {rate.toFixed(2)}×</span>
              <input
                type="range"
                min="0.7" max="1.5" step="0.05"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                className="w-full accent-[#D4AF37]"
                data-testid="module-audio-rate"
              />
            </label>
          </div>

          <p className="text-[10px] text-white/35 mt-3 italic leading-snug">
            Browser-Stimme — gratis & privat. Premium-Stimme folgt sobald ElevenLabs/OpenAI Keys angebunden sind.
          </p>
        </div>
      )}
    </div>
  );
}
